/* script-captions.js — subtitles from a prompter run.
 *
 * WHY
 * A prompter that records the take already knows two things no speech
 * recogniser has to guess: every word that will be said, and when each one was
 * on the reading line. Turning those into a caption file is the feature the
 * category is sold on — Teleprompter Pro exports SRT "from the scroll timing",
 * "Teleprompter for Video" captions the take "using the timings from your
 * recording", Joe Allen's Video Teleprompter and BIGVU do the same (checked
 * 2026-10-01). /teleprompter recorded takes since 2026-08-23 and offered none,
 * and the request that started this (2026-09-20, «можно сделать поддержку
 * субтитров?») came from someone who had spent the evening in our subtitle tools.
 *
 * THE MODEL OF READING
 * The prompter's own rule, the one its camera tail is built on (see finishRun
 * in the blade): a visual line parked ON the reading line is a line the speaker
 * is about to say. So a row is spoken from the moment it is centred on the
 * reading line until the next row is — the blade turns the scroll position into
 * a fractional word index on that rule (readPos) and samples it every frame.
 * With voice following on, the samples are the recogniser's matches instead.
 * Either way this module only sees {t, p}: time and words read.
 *
 * The captions reflect the run AS IT HAPPENED: words skipped by a jump get no
 * caption, a passage re-read after a rewind gets one per reading, a pause is
 * silence. That is what is in the recording, so that is what has to be on it.
 *
 * WHAT IT DOES NOT CLAIM
 * Timing is the prompter's, not a measurement of the voice: a reader who runs a
 * little behind the line is captioned a little early. Aligning to the audio is
 * possible (public/js/karaoke/karaoke-autosync.js does it for songs) but costs a
 * 190–305 MB model; the page says where its times come from instead.
 *
 *   ScriptCaptions.looksLikeSubtitles(text)        real timecodes in this text?
 *   ScriptCaptions.wordTimes(samples, {wpm})       → [{i, start, end}] ms
 *   ScriptCaptions.toCues(readings, words, opts)   → [{start, end, text}] seconds
 *
 * Line breaking is auto-captions' (public/js/auto-captions-smart-break.js —
 * punctuation and conjunctions, one owner); output goes through
 * window.SubtitleSerializer. Tests: tests/js/script-captions.test.mjs.
 */
(function (root) {
    'use strict';

    /* ── "is this pasted text a subtitle file?" ─────────────────────────────
       A cheap, synchronous gate in front of the subtitle engine, because a paste
       handler has to decide before it can await anything. It looks for things
       no prompter script contains — an SRT/VTT arrow between two timecodes, an
       ASS Dialogue line, SBV/LRC/MicroDVD timing — and nothing else: the engine's
       own detector also recognises CSV by a header word, and "Начало, …" in an
       ordinary script must not be taken for a spreadsheet of subtitles. */
    var ARROW = /(?:^|\n)[ \t]*(?:\d{1,3}:)?\d{1,2}:\d{2}[.,]\d{1,3}[ \t]*-{2,}>[ \t]*(?:\d{1,3}:)?\d{1,2}:\d{2}[.,]\d{1,3}/g;
    function count(re, s) { var m = s.match(re); return m ? m.length : 0; }
    function looksLikeSubtitles(text) {
        var s = String(text == null ? '' : text);
        if (!s.trim()) return false;
        if (/^\uFEFF?\s*WEBVTT\b/.test(s)) return true;
        if (/^\uFEFF?\s*\[Script Info\]/i.test(s)) return true;
        if (count(ARROW, s) >= 1) return true;
        if (count(/^[ \t]*Dialogue[ \t]*:/gim, s) >= 2) return true;
        if (count(/^\d{1,3}:\d{2}:\d{2}\.\d{3},\d{1,3}:\d{2}:\d{2}\.\d{3}[ \t]*$/gm, s) >= 2) return true;   // SBV
        if (count(/^\[\d{1,3}:\d{1,2}[.:]\d{1,3}\]/gm, s) >= 3) return true;                                // LRC
        if (count(/^\{[ \t]*\d+[ \t]*\}\{[ \t]*\d+[ \t]*\}/gm, s) >= 2) return true;                          // MicroDVD
        return false;
    }

    /* ── the reading timeline → when each word was read ─────────────────────
       samples: [{t, p, cut?}] in time order — t in ms, p the reading position in
       words (p = i: word i is next; p = i + 1: word i is done), cut = an explicit
       jump starts a new reading pass.

       A pass is a stretch the reading line moved through continuously. It ends
       at an explicit cut, at a step back of more than half a word (rewind,
       auto-loop wrap), or at a leap of more than JUMP_WORDS inside one frame
       (a seek forward — those words were skipped, not read). */
    var REWIND_WORDS = 0.5, JUMP_WORDS = 3, JUMP_MS = 300;

    function wordTimes(samples, opts) {
        var o = opts || {};
        var typ = 60000 / Math.max(40, +o.wpm || 150);     // ms one word takes at the dial
        var passes = [], pass = null, hi = -Infinity;
        for (var k = 0; k < (samples || []).length; k++) {
            var s = samples[k];
            if (!s || !isFinite(s.t) || !isFinite(s.p)) continue;
            var start = !pass || s.cut;
            if (!start) {
                var last = pass[pass.length - 1];
                if (s.p < hi - REWIND_WORDS) start = true;
                else if (s.p - hi > JUMP_WORDS && s.t - last.t < JUMP_MS) start = true;
            }
            if (start) { pass = [{ t: s.t, p: s.p }]; passes.push(pass); hi = s.p; continue; }
            var p = Math.max(hi, s.p), prev = pass[pass.length - 1];
            /* A burst: several words reported at once after a silence — the
               recogniser's way. They were said just now, at speaking pace, not
               spread over the silence before them. */
            var need = (p - prev.p) * typ;
            if (p > prev.p && s.t - prev.t > need * 1.6 + 400) pass.push({ t: s.t - need, p: prev.p });
            pass.push({ t: s.t, p: p });
            hi = p;
        }

        var out = [];
        passes.forEach(function (ps) {
            if (ps.length < 2) return;
            var p0 = ps[0].p, p1 = ps[ps.length - 1].p;
            for (var i = Math.max(0, Math.floor(p0)); i < Math.ceil(p1); i++) {
                // Read in this pass = the line crossed the word's middle.
                if (i + 0.5 < p0 || i + 0.5 > p1) continue;
                var c = timeAt(ps, i + 0.5);
                var a = timeAt(ps, Math.max(i, p0)), b = timeAt(ps, Math.min(i + 1, p1));
                /* A pause with the line inside a word stretches that word over
                   the pause; nobody holds a word for ten seconds. The word keeps
                   the moment its middle was reached, at speaking length. */
                var st = Math.max(a, c - typ / 2), en = Math.min(b, c + typ / 2);
                if (!(en > st)) en = st + typ / 2;
                out.push({ i: i, start: st, end: en });
            }
        });
        out.sort(function (x, y) { return x.start - y.start || x.i - y.i; });
        return out;
    }

    /* First moment the pass reached position x (the pass is monotone in p). */
    function timeAt(ps, x) {
        if (x <= ps[0].p) return ps[0].t;
        var lo = 0, hi = ps.length - 1;
        if (x > ps[hi].p) return ps[hi].t;
        while (lo < hi) { var mid = (lo + hi) >> 1; if (ps[mid].p >= x) hi = mid; else lo = mid + 1; }
        var b = ps[lo], a = ps[lo - 1];
        if (!a || b.p <= a.p) return b.t;
        return a.t + (x - a.p) / (b.p - a.p) * (b.t - a.t);
    }

    /* ── word readings → caption cues ───────────────────────────────────────
       words: [{text, line, speaker}] by script word index. A cue never spans two
       script lines (a line is one speaker's one thought), never bridges a pause
       longer than gapMs, and is broken by the shared smart-break — after
       punctuation, before a conjunction, within maxChars.

       opts.origin / opts.until (ms, same clock as the samples) cut the captions
       to one recording: a take's subtitles start at the take's first frame. */
    function toCues(readings, words, opts) {
        var o = opts || {};
        var gapMs = o.gapMs || 700, maxChars = o.maxChars || 40, maxWords = o.maxWords || 8;
        var minMs = o.minMs || 800;
        var origin = +o.origin || 0;
        var until = (o.until != null && isFinite(o.until)) ? +o.until : Infinity;
        var sb = o.breaker || (root && root.__acSmartBreak && root.__acSmartBreak.reflow);
        var breaker = typeof sb === 'function' ? sb : chunk;

        var runs = [], run = null;
        (readings || []).forEach(function (r) {
            var w = words && words[r.i];
            if (!w || !w.text) return;
            if (r.end <= origin || r.start >= until) return;
            var prev = run && run[run.length - 1];
            if (!prev || r.i !== prev.i + 1 || words[prev.i].line !== w.line || r.start - prev.end > gapMs) {
                run = []; runs.push(run);
            }
            run.push({ i: r.i, text: String(w.text), start: r.start, end: r.end, speaker: w.speaker || '' });
        });

        var cues = [];
        runs.forEach(function (rw) {
            (breaker(rw, maxChars, maxWords) || []).forEach(function (ln) {
                var ws = ln.words || [];
                if (!ws.length) return;
                cues.push({
                    start: ws[0].start, end: ws[ws.length - 1].end,
                    text: ws.map(function (x) { return x.text; }).join(' '),
                    speaker: ws[0].speaker || ''
                });
            });
        });

        /* Shape for reading: close hairline gaps (a caption that blinks off for
           a tenth of a second is noise) and give a short cue time to be read,
           without ever running into the next one. */
        for (var k = 0; k < cues.length; k++) {
            var c = cues[k], n = cues[k + 1];
            if (n && n.start > c.end && n.start - c.end < 250) c.end = n.start;
            if (c.end - c.start < minMs) {
                var want = c.start + minMs;
                c.end = n ? Math.max(c.end, Math.min(want, n.start)) : want;
            }
        }

        var out = [];
        cues.forEach(function (c) {
            var st = Math.max(c.start, origin), en = Math.min(c.end, until);
            if (!(en > st)) return;
            out.push({ start: (st - origin) / 1000, end: (en - origin) / 1000, text: c.text, speaker: c.speaker });
        });
        return out;
    }

    /* Only if the shared breaker failed to load: fixed chunks, still bounded. */
    function chunk(ws, maxChars, maxWords) {
        var lines = [], cur = [], len = 0;
        ws.forEach(function (w) {
            var add = (cur.length ? 1 : 0) + String(w.text).length;
            if (cur.length && (len + add > maxChars || cur.length >= maxWords)) { lines.push({ words: cur }); cur = []; len = 0; add = String(w.text).length; }
            cur.push(w); len += add;
        });
        if (cur.length) lines.push({ words: cur });
        return lines;
    }

    var api = { looksLikeSubtitles: looksLikeSubtitles, wordTimes: wordTimes, toCues: toCues, _timeAt: timeAt };
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.ScriptCaptions = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null));
